var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { bind, singleton } from '../decorators';
import { generateUuid } from '../utils';
import { AuthClient } from './clients/Auth.client';
import { FileService } from './services/File.service';
import { LogService } from './services/Log.service';
import { AuthorizationService } from './services/Authorization.service';
import { CostService } from './services/Cost.service';
import { TransactionService } from './services/Transaction.service';
import { MediaService } from './services/Media.service';
import { UserService } from './services/User.service';
import { PingService } from './services/Ping.service';
import { WalletService } from './services/Wallet.service';
import { OrganizationService } from './services/Organization.service';
import { OrganizationDepartmentService } from './services/OrganizationDepartment.service';
import { DepartmentPositionService } from './services/DepartmentPosition.service';
import { TrelloService } from './services/Trello.service';
/**
 * MasterDataSDK
 * @description
 * The SDK for the MasterData API
 * @example
 * const sdk = new MasterDataSDK('https://api.neko.com');
 * sdk.user.getUsers();
 * sdk.authorization.getRoles();
 */
let MasterDataSDK = class MasterDataSDK {
    api;
    id;
    user;
    organization;
    organizationDepartment;
    departmentPosition;
    authorization;
    cost;
    transaction;
    wallet;
    media;
    file;
    ping;
    log;
    trello;
    authClient = null;
    constructor(api, id = generateUuid()) {
        this.api = api;
        this.id = id;
        // Bind getAuthHeader to preserve 'this' context when passed to services
        const boundGetAuthHeader = () => this.getAuthHeader();
        this.authorization = new AuthorizationService(this.api, boundGetAuthHeader);
        this.cost = new CostService(this.api, boundGetAuthHeader);
        this.user = new UserService(this.api, boundGetAuthHeader);
        this.transaction = new TransactionService(this.api, boundGetAuthHeader);
        this.media = new MediaService(this.api, boundGetAuthHeader);
        this.file = new FileService(this.api, boundGetAuthHeader);
        this.ping = new PingService(this.api);
        this.wallet = new WalletService(this.api, boundGetAuthHeader);
        this.log = new LogService(this.api, boundGetAuthHeader);
        this.trello = new TrelloService(this.api, boundGetAuthHeader);
        this.organization = new OrganizationService(this.api, boundGetAuthHeader);
        this.organizationDepartment = new OrganizationDepartmentService(this.api, boundGetAuthHeader);
        this.departmentPosition = new DepartmentPositionService(this.api, boundGetAuthHeader);
    }
    createAuthClient(configs, onAuthenticated) {
        if (!this.authClient) {
            const processedConfigs = {
                ...configs,
                authenticationService: this.user,
                authorizationService: this.authorization,
            };
            this.authClient = new AuthClient(processedConfigs, (accessToken) => {
                onAuthenticated?.(accessToken);
            });
        }
        return this.authClient;
    }
    async getAuthHeader() {
        const accessToken = await this.authClient?.getAccessToken();
        if (!accessToken) {
            return {};
        }
        return {
            Authorization: `Bearer ${accessToken}`,
        };
    }
};
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, Function]),
    __metadata("design:returntype", void 0)
], MasterDataSDK.prototype, "createAuthClient", null);
__decorate([
    bind,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", Promise)
], MasterDataSDK.prototype, "getAuthHeader", null);
MasterDataSDK = __decorate([
    singleton,
    __metadata("design:paramtypes", [String, Object])
], MasterDataSDK);
export { MasterDataSDK };
//# sourceMappingURL=data:application/json;base64,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